import React from "react";
import {
  FileText,
  ListChecks,
  Package,
  TrendingDown,
  Boxes,
} from "lucide-react";
import type { Product, ProductTabsProps, VariantSku } from "./types";

const cardBase =
  "bg-section-surface p-4 rounded-lg border border-section-border";
const cardLabel = "flex items-center gap-2 text-accent mb-2";
const rowHover = "hover:bg-section-surface";
const cellKey =
  "px-4 py-3 text-sm font-medium text-section-muted bg-section-surface";
const cellVal = "px-4 py-3 text-sm text-section-text font-semibold";
const pill =
  "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
const pillIn = `${pill} bg-primary/10 text-primary border border-primary/20`;
const pillOut = `${pill} bg-section-surface text-section-muted border border-section-border`;
const pillLow = `${pill} bg-accent/10 text-accent border border-accent/30`;

const RawSpecs: React.FC<{
  product: Product;
  hasDiscount: boolean;
  originalPrice: number;
}> = ({ product, hasDiscount, originalPrice }) => {
  const qty = typeof product.quantity === "number" ? product.quantity : 0;
  const unlimited = !!product.is_unlimited_stock;
  const validWeight = typeof product.weight === "number" && product.weight > 0;

  const stockCard = unlimited ? (
    <p className="text-2xl font-bold text-section-text">In Stock</p>
  ) : qty === 0 ? (
    <p className="text-2xl font-bold text-section-muted">Out of Stock</p>
  ) : qty < 5 ? (
    <p className="text-2xl font-bold text-accent">Low Stock</p>
  ) : (
    <p className="text-2xl font-bold text-section-text">{qty} Units</p>
  );


  return (
    <div>
      <h3 className="text-xl font-bold text-section-text mb-6 flex items-center gap-2">
        <ListChecks className="w-5 h-5 text-primary" />
        Product Specifications
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className={cardBase}>
          <div className={cardLabel}>
            <Package className="w-5 h-5" />
            <span className="text-sm font-medium">Price</span>
          </div>
          <p className="text-2xl font-bold text-section-text">
            ${originalPrice.toFixed(2)}
          </p>
        </div>

        {hasDiscount && (
          <div className={cardBase}>
            <div className={cardLabel}>
              <TrendingDown className="w-5 h-5" />
              <span className="text-sm font-medium">Discount</span>
            </div>
            <p className="text-2xl font-bold text-accent">
              {product.sale_rate}% OFF
            </p>
          </div>
        )}

        <div className={cardBase}>
          <div className={cardLabel}>
            <Boxes className="w-5 h-5" />
            <span className="text-sm font-medium">Stock</span>
          </div>
          {stockCard}
        </div>
      </div>

      {(validWeight || product.brand_name) && (
        <div className="border border-section-border rounded-lg overflow-hidden">
          <table className="w-full">
            <tbody className="divide-y divide-section-border">
              {validWeight && (
                <tr className={rowHover}>
                  <td className={`${cellKey} w-1/3`}>Weight</td>
                  <td className={cellVal}>{product.weight} Kg</td>
                </tr>
              )}
              {product.brand_name && (
                <tr className={rowHover}>
                  <td className={cellKey}>Brand</td>
                  <td className={cellVal}>{product.brand_name}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const skuStockLabel = (s: VariantSku): React.ReactNode => {
  if (s.is_unlimited_stock) return <span className={pillIn}>Unlimited</span>;
  if (s.stock_status === "out_of_stock" || s.quantity === 0)
    return <span className={pillOut}>Out of stock</span>;
  if (s.quantity < 5)
    return <span className={pillLow}>Low ({s.quantity})</span>;
  return <span className={pillIn}>In stock ({s.quantity})</span>;
};

const VariantSpecs: React.FC<{
  product: Product;
}> = ({ product }) => {
  const skus = product.variant_tree?.skus ?? [];
  const types = product.variant_tree?.types ?? [];
  const typeNames = types.map((t) => t.name);

  const skuPrices = skus.map((s) => s.usd_price).filter((p) => typeof p === "number");
  const minP =
    typeof product.min_variant_price === "number"
      ? product.min_variant_price
      : skuPrices.length
        ? Math.min(...skuPrices)
        : undefined;
  const maxP =
    typeof product.max_variant_price === "number"
      ? product.max_variant_price
      : skuPrices.length
        ? Math.max(...skuPrices)
        : undefined;
  const priceRangeLabel =
    typeof minP === "number" && typeof maxP === "number"
      ? minP === maxP
        ? `$${minP.toFixed(2)}`
        : `$${minP.toFixed(2)} – $${maxP.toFixed(2)}`
      : "—";

  const inStockCount = skus.filter(
    (s) => s.is_unlimited_stock || (s.stock_status !== "out_of_stock" && s.quantity > 0),
  ).length;

  return (
    <div>
      <h3 className="text-xl font-bold text-section-text mb-6 flex items-center gap-2">
        <ListChecks className="w-5 h-5 text-primary" />
        Variant Specifications
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className={cardBase}>
          <div className={cardLabel}>
            <Package className="w-5 h-5" />
            <span className="text-sm font-medium">Price Range</span>
          </div>
          <p className="text-2xl font-bold text-section-text">{priceRangeLabel}</p>
        </div>

        <div className={cardBase}>
          <div className={cardLabel}>
            <Boxes className="w-5 h-5" />
            <span className="text-sm font-medium">Variants</span>
          </div>
          <p className="text-2xl font-bold text-section-text">
            {inStockCount}/{skus.length} in stock
          </p>
        </div>

        {product.brand_name && (
          <div className={cardBase}>
            <div className={cardLabel}>
              <TrendingDown className="w-5 h-5" />
              <span className="text-sm font-medium">Brand</span>
            </div>
            <p className="text-2xl font-bold text-section-text">
              {product.brand_name}
            </p>
          </div>
        )}
      </div>

      {skus.length > 0 && (
        <div className="border border-section-border rounded-lg overflow-x-auto">
          <table className="w-full">
            <thead className="bg-section-surface">
              <tr>
                {typeNames.map((name) => (
                  <th
                    key={name}
                    className="px-4 py-3 text-left text-xs font-semibold text-section-muted uppercase tracking-wider"
                  >
                    {name}
                  </th>
                ))}
                <th className="px-4 py-3 text-left text-xs font-semibold text-section-muted uppercase tracking-wider">
                  SKU
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-section-muted uppercase tracking-wider">
                  Price
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-section-muted uppercase tracking-wider">
                  Discount
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-section-muted uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-section-muted uppercase tracking-wider">
                  Weight
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-section-border">
              {skus.map((s) => (
                <tr key={s.id} className={rowHover}>
                  {typeNames.map((name) => (
                    <td
                      key={name}
                      className="px-4 py-3 text-sm text-section-text font-medium"
                    >
                      {s.options?.[name] ?? "—"}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-sm text-section-muted font-mono">
                    {s.sku ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-section-text font-semibold">
                    ${s.usd_price.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-sm text-accent font-semibold">
                    {s.sale_rate > 0 ? `${s.sale_rate}% OFF` : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm">{skuStockLabel(s)}</td>
                  <td className="px-4 py-3 text-sm text-section-text">
                    {typeof s.weight === "number" && s.weight > 0
                      ? `${s.weight} Kg`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ProductTabs: React.FC<ProductTabsProps> = ({
  product,
  selectedTab,
  onTabChange,
  hasDiscount,
  originalPrice,
}) => {
  return (
    <div className="bg-section-card-bg rounded-xl shadow-sm mb-8 border border-section-border overflow-hidden">
      <div className="border-b border-section-border">
        <nav className="flex px-6 -mb-px">
          <button
            onClick={() => onTabChange("description")}
            className={`relative py-4 px-4 font-medium text-sm transition-colors flex items-center gap-2 ${
              selectedTab === "description"
                ? "text-primary"
                : "text-section-muted hover:text-section-text"
            }`}
          >
            <FileText className="w-4 h-4" />
            <span>Description</span>
            {selectedTab === "description" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
          <button
            onClick={() => onTabChange("specifications")}
            className={`relative py-4 px-4 font-medium text-sm transition-colors flex items-center gap-2 ${
              selectedTab === "specifications"
                ? "text-primary"
                : "text-section-muted hover:text-section-text"
            }`}
          >
            <ListChecks className="w-4 h-4" />
            <span>Specifications</span>
            {selectedTab === "specifications" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
        </nav>
      </div>

      <div className="p-6">
        {selectedTab === "description" && (
          <div>
            <h3 className="text-xl font-bold text-section-text mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Product Description
            </h3>
            <p className="text-section-muted leading-relaxed text-base">
              {product.description ||
                "No description available for this product."}
            </p>
          </div>
        )}

        {selectedTab === "specifications" &&
          (product.has_variants ? (
            <VariantSpecs product={product} />
          ) : (
            <RawSpecs
              product={product}
              hasDiscount={hasDiscount}
              originalPrice={originalPrice}
            />
          ))}
      </div>
    </div>
  );
};

export default ProductTabs;
